import React, { useEffect, useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { arrayMove } from '@dnd-kit/sortable';

import { styled } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import LoadingButton from '@mui/lab/LoadingButton';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';

import DndContainer from 'components/DndContainer';
import DragHandle from 'components/DragHandle';
import SelectCustomerDialog from 'components/SelectCustomerDialog';
import { firestoreListener } from 'modules/firebase';
import { callFunction } from 'modules/firebase';
import PrintCustomerData from 'pages/Customers/PrintCustomerData';
import { getPrintFields } from 'modules/customer';

const DividerText = styled('div')(({ theme }) => ({
  position: 'absolute',
  height: '44px',
  top: '-22px',
  backgroundColor: '#fff',
  paddingRight: '8px',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  alignItems: 'center',
}));

const rowStyle = {
  borderTop: 'solid 1px #efefef',
  width: '100%',
  height: '80px',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: '#fff',
  padding: '0px 16px 0px 8px',
  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.2)',
  zIndex: 10000,
}

const cellStyle = {
  marginLeft: '8px',
  marginRight: '8px',
  flexGrow: 2
}

function SortableItem({ item, index, onChange, fields }) {
  const { formatMessage } = useIntl()

  function createField(field) {
    if (field.type === 'text') {
      return (
        <Grid item key={field.name} xs={field.xs}>
          <TextField
            disabled
            type="text"
            size="small"
            label={formatMessage({ id: 'customer.print.field' }) }
            variant="outlined"
            value={item.ext ? item.name : item.roots ? formatMessage({ id: `customer.profile.${item.name}.roots` }) : formatMessage({ id: `customer.profile.${item.name}` })}
            // value={item[field.name]}
            onChange={e => onChange(index, { name: field.name, required: field.required }, e.target.value)}
            error={item[`${[field.name]}_err`] ? true : false}
            helperText={item[`${[field.name]}_err`]}
            fullWidth
          />
        </Grid>
      );
    } else if (field.type === 'text-select') {
      return (
        <Grid item key={field.name} xs={field.xs}>
          <TextField
            select
            type="text"
            size="small"
            label={formatMessage({ id: 'customer.print.size' })}
            variant="outlined"
            fullWidth
            onChange={e => onChange(index, { name: field.name }, e.target.value)}
            value={item[field.name]}
          >
            {field.selectFields.map(select => {
              return <MenuItem key={select.id} disabled={select.disabled} value={select.id}>{select.name}</MenuItem>
            })}
          </TextField>
        </Grid>
      );
    } else if (field.type === 'switch') {
      return (
        <Grid item key={field.name} xs={field.xs}>
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', fontSize: '15px' }}>
            <div style={{ whiteSpace: 'nowrap' }}><FormattedMessage id="customer.print.off" /></div>
            <Switch
              checked={item[field.name]}
              onChange={e => onChange(index, { name: field.name }, e.target.checked)}
              color="primary"
              inputProps={{ 'aria-label': 'secondary checkbox' }}
            />
            <div style={{ whiteSpace: 'nowrap' }}><FormattedMessage id="customer.print.on" /></div>
          </div>
        </Grid>
      )
    } else if (field.type === 'space') {
      return <Grid item key={field.name} xs={field.xs}></Grid>
    }
  }

  return (
    <div style={rowStyle}>
      <DragHandle />
      <div style={cellStyle}>
        <Grid container spacing={3}>
          {fields.map(field => createField(field))}
        </Grid>
      </div>
    </div>
  )
}

function EditCustomerPrint() {
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const config = useSelector(state => state.config.data)

  const customerType = Object.keys(config.customerType || {}).filter(i => config.customerType[i].active).map(i => config.customerType[i])
  const customerTypeMapping = customerType.reduce((acc, cur) => { acc[cur.id] = cur; return acc }, {})
  const userMapping = useSelector(state => state.users.data);
  const extDataMapping = useSelector(state => state.customerExtTab.data)
  // const customerExtTab = Object.keys(config.customerExtTab || {}).map(p => config.customerExtTab[p]).filter(i => i.active && !i.related)
  const [customers, setCustomers] = useState([]);
  const [customerMapping, setCustomerMapping] = useState({})
  const [customer, setCustomer] = useState(null)
  const [customerExtData, setCustomerExtData] = useState(null);
  const [openDialog, setOpenDialog] = useState('')
  const [loading, setLoading] = useState(false)

  const [printFields, setPrintFields] = useState({ basic: [],ice: [],ext: [] })
  const customerData = customer ? formatData(customer) : null

  useEffect(() => {
    const unsubscribe = firestoreListener({
      collection: 'customers',
      mapping: true,
      array: true,
      onData: ({ mapping, data }) => {
        setCustomers(data)
        setCustomerMapping(mapping)
      }
    })

    return () => unsubscribe()
  }, []);

  useEffect(() => {
    const unsubscribe = customer ? firestoreListener({
      collection: 'customerExtData',
      doc: customer.id,
      unwrap: true,
      onData: (data) => {
        setCustomerExtData(data)
      }
    }) : null

    return () => unsubscribe?.()
  }, [customer]);

  useEffect(() => {
    if (extDataMapping) {
      setPrintFields( data => {
        return getPrintFields(extDataMapping, config.customerExtTab, config.printCustomerData)
      })
    }
    return () => {}
  }, [extDataMapping]);

  function handleDragEnd(groupName, groupIndex, event) {
    const { active, over } = event;
    if (active.id !== over.id) {
      if (groupName === 'ext') {
        const oldIndex = printFields[groupName][groupIndex].fields.findIndex(e => e.id === active.id);
        const newIndex = printFields[groupName][groupIndex].fields.findIndex(e => e.id === over.id);
        const array = printFields[groupName]
        array[groupIndex].fields = arrayMove(array[groupIndex].fields, oldIndex, newIndex)
        updateItems(groupName, [...array])
      } else {
        const oldIndex = printFields[groupName].findIndex(e => e.id === active.id);
        const newIndex = printFields[groupName].findIndex(e => e.id === over.id);
        updateItems(groupName, arrayMove(printFields[groupName], oldIndex, newIndex))
      }
    }
  }

  const updateItems = (name, value) => {
    setPrintFields((currentValue) => {
      const newValue = {
        ...currentValue,
        [name]: value,
      }
      return newValue
    })
  }

  function onSelectCustomer(customer, params) {
    if (params === 'customer') {
      setCustomer(customerMapping[customer.id])
    }
  }

  const fields = [
    { name: 'field', type: 'text', xs: 5, required: false },
    { name: 'size', type: 'text-select', selectFields: [
      { name: '1', id: 1 },
      { name: '2', id: 2 },
      { name: '3', id: 3 },
      { name: '4', id: 4 },
    ], xs: 3, required: false },
    { name: 'active', type: 'switch', xs: 4, required: false }
  ]

  function updateCurrentData(groupName, groupIndex, index, field, value) {
    if (groupName === 'ext') {
      const array = printFields[groupName]
      const newData = array[groupIndex]
      newData.fields[index] = {
        ...newData.fields[index],
        [field.name]: value
      }
      array[groupIndex].fields = newData.fields
      // 如果此欄位是 checkbox, 當這個欄位不列印時, 將關聯的欄位一併取消列印
      if (newData.fields[index].type === 'check') {
        const relIndex = newData.fields.findIndex(i => i.parent === newData.fields[index].check)
        if (relIndex !== -1) {
          newData.fields[relIndex] = {
            ...newData.fields[relIndex],
            active: false
          }
        }
      }

      updateItems(groupName, [...array])
    } else {
      const newData = [...printFields[groupName]]
      newData[index] = {
        ...newData[index],
        [field.name]: value
      }
      updateItems(groupName, newData)
    }
  }

  // const formatData = (customer) => {
  //   const newData = { ...customer }
  //   newData.salesRepName = newData.salesRep ? userMapping[newData.salesRep].displayName : ''
  //   newData.lastAppointmentDate = newData.lastAppointmentDateTime ? newData.lastAppointmentDateTime.split('_')[0] : ''

  //   return newData
  // }

  // const handleRequestSort = (event, property) => {
  //   const isAsc = orderBy === property && order === 'asc';
  //   setOrder(isAsc ? 'desc' : 'asc');
  //   setOrderBy(property);
  // };

  function formatData(customer) {
    const newData = { ...customer }
    newData.salesRepName = userMapping[newData.salesRep] ? userMapping[newData.salesRep].displayName : ''

    return newData
  }

  async function handleSave() {
    setLoading(true)

    let newData = {
      basic: printFields.basic.map( i => ({
        id: i.id,
        size: i.size,
        active: i.active,
      })),
      ice: printFields.ice.map( i => ({
        id: i.id,
        size: i.size,
        active: i.active,
      })),
      ext: printFields.ext.reduce((acc, cur) => {
        acc[cur.id] = cur.fields.map( i => ({
          id: i.id,
          size: i.size,
          active: i.active,
        }))
        return acc
      }, {}),
    }

    try {
      // console.log(JSON.stringify(newData, null, 4))
      await callFunction('saveConfigurations', { type: 'printCustomerData', ...newData })
    } catch (ex) {
      console.log(ex)
    }
    handleClose()
  }

  function handleClose() {
    navigate('/setting/customer/print');
  }

  return (
    <Box p={2} style={{ marginTop: '20px' }}>
      <Typography sx={{ flex: '1 1 20%' }} variant="h6" id="tableTitle" component="div">
        {formatMessage({ id: 'setCustomer.print.edit.title' })}
      </Typography>
      <Paper style={{ marginTop: '20px' }}>
        {openDialog === 'customer' && <SelectCustomerDialog
          handleClose={() => setOpenDialog('')}
          handleSave={onSelectCustomer}
          customers={customers || []}
          dialogTitle={formatMessage({ id: 'selectCustomerDialog.title' })}
          params={openDialog}
        />}
        <Grid container spacing={0}>
          <Grid item xs={6}>
            <div style={{ padding: '8px 28px 8px 8px', height: 'calc(100vh - 230px)',overflowY: 'auto' }}>
              <div>
                <div style={{ position: 'relative' }}>
                  <Divider sx={{ mt: '0px', mb: '22px' }} />
                  <DividerText>
                    <Typography color="textSecondary" variant="caption"><FormattedMessage id="customer.profile.basic" /></Typography>
                  </DividerText>
                </div>
                <DndContainer
                  dargger
                  items={printFields.basic}
                  handleDragEnd={(event) => handleDragEnd('basic', -1, event)}
                  ChildItem={SortableItem}
                  onChange={(index, field, value) => updateCurrentData('basic', -1, index, field, value)}
                  fields={fields}
                />
              </div>
              <div>
                <div style={{ position: 'relative' }}>
                  <Divider sx={{ mt: '22px', mb: '22px' }} />
                  <DividerText>
                    <Typography color="textSecondary" variant="caption"><FormattedMessage id="customer.profile.emergencyContacts.root" /></Typography>
                  </DividerText>
                </div>
                <DndContainer
                  dargger
                  items={printFields.ice}
                  handleDragEnd={(event) => handleDragEnd('ice', -1, event)}
                  ChildItem={SortableItem}
                  onChange={(index, field, value) => updateCurrentData('ice', -1, index, field, value)}
                  fields={fields}
                />
              </div>
              {printFields.ext.map((i, groupIndex) => (
                <div key={`divider-${i.id}`} >
                  <div style={{ position: 'relative' }}>
                    <Divider sx={{ mt: '22px', mb: '22px' }} />
                    <DividerText>
                      <Typography color="textSecondary" variant="caption">{i.name}</Typography>
                    </DividerText>
                  </div>
                  <DndContainer
                    key={i.id}
                    dargger
                    items={i.fields}
                    handleDragEnd={(event) => handleDragEnd('ext', groupIndex, event)}
                    ChildItem={SortableItem}
                    onChange={(index, field, value) => updateCurrentData('ext', groupIndex, index, field, value)}
                    fields={fields}
                  />
                </div>

              ))}
            </div>
          </Grid>
          <Grid item xs={6}>
            <div style={{ padding : '8px 16px' }}>
              <TextField
                required
                type="text"
                size="small"
                label={formatMessage({ id: 'setCustomer.print.preview' })}
                variant="outlined"
                value={customer ? customer.name : ''}
                onClick={() => { setOpenDialog('customer') }}
                fullWidth
              />
            </div>
            <div style={{ padding: '0px', height: 'calc(100vh - 290px)', overflowY: 'auto' }}>
              {customerData && <PrintCustomerData
                printFields={printFields}
                customerData={customerData}
                customerExtData={customerExtData}
                userMapping={userMapping}
                customerMapping={customerMapping}
                customerTypeMapping={customerTypeMapping}
              />}
            </div>
          </Grid>

        </Grid>
      </Paper>
      <Stack spacing={1} direction="row" sx={{ justifyContent: 'flex-end', position: 'absolute', bottom: '16px', right: '16px' }}>
        <Button variant="contained" color="primary" onClick={handleClose}>
          <FormattedMessage id="button.cancel" />
        </Button>
        <LoadingButton
          color="primary"
          onClick={handleSave}
          disabled={loading}
          loading={loading}
          loadingPosition="start"
          loadingIndicator={<CircularProgress size={24} />}
          startIcon={<div />}
          variant="contained"
        >
          <FormattedMessage id="button.save" />
        </LoadingButton>
      </Stack>
    </Box>
  );
}

export default EditCustomerPrint;
